// ConversionTrustSection — PAWTENANT-CONVERSION-TRUST-STRIPS-TEST-001.
//
// ONE shared, non-interactive trust section mounted directly below the hero on
// every PawTenant conversion route. Two layers:
//
//   Strip 1 (dark, deep PawTenant tone) — four OPERATIONAL trust signals.
//   Strip 2 (light)                     — credential / external trust marks.
//
// ── HARD RULES (enforced by scripts/check-conversion-trust-section.mjs) ──────
//
//  * NEARLY NON-INTERACTIVE. No <button>, no onClick, no tabIndex, no
//    interactive ARIA role. The ONLY clickable things in the whole section are
//    the review marks flagged `linked`, and each one may point at exactly ONE
//    destination: its own `evidence` URL, the page a visitor uses to check the
//    claim. Owner decision, 2026-09-22 — Trustpilot's brand rules expect the
//    mark to link, and sending someone to the proof is the opposite of the
//    problem this section guards against. Nothing else may become clickable.
//  * NO REMOTE ASSETS. Every mark is an inline SVG or a Remix icon that already
//    ships with the app. No <img src="http…">, no CDN, no tracking pixel, no
//    third-party script.
//  * NO STRUCTURED DATA. This component emits no JSON-LD. Review schema,
//    aggregateRating and `sameAs` entries must never be added because a mark is
//    visible here — visible ≠ endorsed, and schema is a machine-readable claim.
//  * NO MOTION. No carousel, no autoplay, no transition that moves layout.
//
// ── CLAIM EVIDENCE (why each line is allowed to exist) ──────────────────────
//
//  "All 50 States / State-matched licensed care"
//      src/config/seoConfig.ts ("licensed professionals in all 50 US states")
//      + src/pages/home/components/HeroSection.tsx ("Serving all 50 US states").
//  "Licensed Clinical Review / Independent eligibility decision"
//      src/mocks/doctors.ts carries per-provider NPI + state license numbers;
//      /our-providers publishes them. The decision is the provider's, and a
//      non-qualifying review is refunded — hence "independent", never
//      "guaranteed".
//  "Verifiable Letter / Provider and license details included"
//      src/pages/esa-letter-verification/page.tsx — the landlord-facing check
//      returns provider name, NPI and state license number.
//  "24-Hour Delivery / After clinical approval"
//      PawTenant's current approved promise, guarded by
//      scripts/check-customer-delivery-24h-promise.mjs. The qualifier is part
//      of the promise: the two must always ship as a pair, and "24-48 hours"
//      is a regression, not a softening.
//  "Licensed Clinicians" / "Secure and Private Process"
//      Providers are state-licensed (above). The privacy detail remains unchanged.
//  "HIPAA Compliant" badge
//      Owner-requested brand statement, 2026-10-15. This is not an independent
//      audit, certification or government seal. Do not add certification claims.
//  "Reviewed on Trustpilot"
//      https://www.trustpilot.com/review/pawtenant.com — a real, CLAIMED
//      PawTenant business profile (claimed December 2024), and the same review
//      destination PawTenant's own customer comms already use
//      (src/config/portalLinks.ts REVIEW_LINKS.trustpilot). NO score and NO
//      review count are rendered: a hardcoded figure goes stale the moment a
//      review lands, and a stale figure is a fabricated one.
//
// ── DELIBERATELY ABSENT ─────────────────────────────────────────────────────
//
//  CBS / MSNBC / FOX / ABC. No coverage of PawTenant by any of them could be
//  found, and this repository's own history is evidence AGAINST it: commit
//  c7c87dc ("Phase 0: Trust + SEO cleanup") removed an "As Seen In" bar with
//  the note "Removed fake media logos, kept trust badges only". MEDIA_LOGOS is
//  therefore an EMPTY allow-list. Adding an entry requires a real, PawTenant-
//  specific evidence URL in `evidence` — the guard fails on any entry whose
//  evidence is missing, and on any outlet name appearing anywhere in this file
//  outside this comment block.
//
//  Refund copy. PawTenant's refund terms are conditional (full refund when the
//  provider does not approve; housing-denial claims are REVIEWED, never
//  automatic — see /no-risk-guarantee and AssessmentTrustStrip). A compact
//  strip cannot carry those conditions without oversimplifying them, so it
//  carries none. Omission is the honest option here.

import type { ReactElement, ReactNode } from "react";

/* ── Strip 1 · operational trust signals ─────────────────────────────────── */

interface OperationalItem {
  /** Stable key — also the guard's handle on the item. */
  key: string;
  /** Remix icon class (outline weight only — the strip stays calm). */
  icon: string;
  label: string;
  detail: string;
}

export const OPERATIONAL_ITEMS: readonly OperationalItem[] = [
  {
    key: "states",
    icon: "ri-map-2-line",
    label: "All 50 States",
    detail: "State-matched licensed care",
  },
  {
    key: "review",
    icon: "ri-stethoscope-line",
    label: "Licensed Clinical Review",
    detail: "Independent eligibility decision",
  },
  {
    key: "verifiable",
    icon: "ri-shield-check-line",
    label: "Verifiable Letter",
    detail: "Provider and license details included",
  },
  {
    key: "delivery",
    icon: "ri-time-line",
    // The promise and its qualifier are one unit. Never "24-48 hours".
    label: "24-Hour Delivery",
    detail: "After clinical approval",
  },
] as const;

/* ── Strip 2 · credentials ───────────────────────────────────────────────── */

interface CredentialItem {
  key: string;
  /**
   * Remix icon class. IT MUST EXIST IN public/assets/remixicon-subset.css —
   * the app ships a SUBSETTED icon font, so a glyph outside the subset renders
   * as blank space with no error anywhere. `ri-qr-scan-2-line` did exactly that
   * here until browser QA caught the empty slot; the guard now checks every
   * icon in this file against the subset.
   */
  icon: string;
  label: string;
  detail: string;
}

export const CREDENTIAL_ITEMS: readonly CredentialItem[] = [
  {
    key: "clinicians",
    icon: "ri-user-heart-line",
    label: "Licensed Clinicians",
    detail: "Evaluations completed by professionals licensed in your state",
  },
  {
    key: "privacy",
    icon: "ri-lock-2-line",
    label: "Secure and Private Process",
    detail: "HIPAA-conscious handling of your health information",
  },
  {
    key: "verification",
    icon: "ri-qr-code-line",
    label: "Independent Letter Verification",
    detail: "Housing providers confirm the letter without seeing your health details",
  },
] as const;

/* ── Strip 2 · external marks (verified allow-list) ──────────────────────── */

interface ExternalMark {
  key: string;
  /** Accessible name for the mark. */
  name: string;
  /** One short, factual line. Never a rating, never a review count. */
  caption: string;
  /**
   * PawTenant-specific evidence for the relationship this mark implies.
   * Required, non-empty, and checked by the guard. A mark with no evidence
   * does not ship — "it was in a competitor screenshot" is not evidence.
   */
  evidence: string;
  /**
   * When true the mark is a link — and its ONLY permitted destination is its
   * own `evidence` URL, i.e. the page a visitor would use to check the claim
   * for themselves. That is the whole point: the mark and the proof are the
   * same click. The guard enforces it, so a mark can never be quietly
   * re-pointed at a campaign page, an affiliate hop or anything else.
   *
   * Review platforms link (Trustpilot's own brand rules expect it). The
   * payment processor does NOT — "who takes the card" is a statement of fact
   * about checkout, not something a visitor verifies on a third-party page.
   */
  linked?: boolean;
}

/**
 * The external marks PawTenant can substantiate. Every one names a relationship
 * that actually exists — a review platform PawTenant asks its own customers to
 * post on, or the payment processor that actually takes the money. None of them
 * asserts that a third party endorsed, rated or covered PawTenant.
 *
 * Each is a wordmark rendered inline in the same self-contained style the repo
 * already uses for card brands (PaymentTrustStrip.tsx): it always renders,
 * stays crisp, never hotlinks and never phones home.
 */
export const EXTERNAL_MARKS: readonly ExternalMark[] = [
  {
    key: "trustpilot",
    name: "Trustpilot",
    caption: "Reviewed on Trustpilot",
    evidence: "https://www.trustpilot.com/review/pawtenant.com",
    linked: true,
  },
  {
    key: "google",
    name: "Google",
    caption: "Reviewed on Google",
    // Owner-supplied Google Reviews destination (portalLinks.ts, 2026-07-09),
    // and the channel PawTenant's own post-delivery review requests point at
    // (TrustpilotReviewPanel.tsx — Google became the primary review channel on
    // 2026-05-19). Like Trustpilot: no star rating, no review count.
    evidence: "https://www.google.com/search?q=PawTenant&sca_esv=e8b19434261761b5&sxsrf=APpeQntGABSGXu5IOMymxIZKMYI-HAh2iw%3A1792082334808&ei=ngHRaoKWMPmTseMP-M6NGQ&biw=1536&bih=695&uact=5&oq=PawTenant&gs_lp=Egxnd3Mtd2l6LXNlcnAiCVBhd1RlbmFudDIFEAAYgAQyBRAAGIAEMgQQABgeMgUQABjvBTIFEAAY7wUyCBAAGIAEGKIEMgUQABjvBTIFEAAY7wVIyw1Q5QdY3wpwAngAkAEAmAHeAaABpAOqAQUwLjEuMbgBA8gBAPgBAZgCBKACwgPCAgsQABiABBiiBBiwA8ICCBAAGO8FGLADmAMAiAYBkAYFkgcFMi4wLjKgB-UGsgcDMi0yuAevA8IHBzAuMS4yLjHIBxWACAE&sclient=gws-wiz-serp",
    linked: true,
  },
  {
    key: "stripe",
    name: "Stripe",
    caption: "Secure checkout by Stripe",
    // Not a review or an endorsement — a statement of fact about who processes
    // the payment. Stripe IS the processor: src/pages/assessment/components/
    // Step3Checkout.tsx and the whole accounts/refund path run on it.
    evidence: "https://stripe.com/",
  },
] as const;

/**
 * Verified media-outlet allow-list. EMPTY on purpose — see "DELIBERATELY
 * ABSENT" above. An entry may only be added together with a PawTenant-specific
 * article/feature URL in `evidence`.
 */
export const MEDIA_LOGOS: readonly ExternalMark[] = [] as const;

/** Trustpilot wordmark — star + word, non-clickable, no score. */
function TrustpilotMark() {
  return (
    <svg
      role="img"
      aria-label="Trustpilot"
      viewBox="0 0 115 24"
      className="h-8 w-auto max-w-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 1.6l3.12 6.32 6.98 1.02-5.05 4.92 1.19 6.95L12 17.54l-6.24 3.27 1.19-6.95L1.9 8.94l6.98-1.02z"
        fill="#00B67A"
      />
      <text
        x="28"
        y="18"
        fontFamily="Helvetica, Arial, sans-serif"
        fontWeight="700"
        fontSize="18"
        letterSpacing="-0.4"
        fill="#191919"
      >
        Trustpilot
      </text>
    </svg>
  );
}

/** Google wordmark — the four brand colours in their real letter order. */
function GoogleMark() {
  const L = [
    ["G", "#4285F4"],
    ["o", "#EA4335"],
    ["o", "#FBBC05"],
    ["g", "#4285F4"],
    ["l", "#34A853"],
    ["e", "#EA4335"],
  ] as const;
  return (
    <svg
      role="img"
      aria-label="Google"
      viewBox="0 0 70 24"
      className="h-8 w-auto max-w-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <text
        x="0"
        y="18"
        fontFamily="Arial, Helvetica, sans-serif"
        fontWeight="500"
        fontSize="21"
        letterSpacing="-0.6"
      >
        {L.map(([ch, fill], i) => (
          <tspan key={`${ch}-${i}`} fill={fill}>
            {ch}
          </tspan>
        ))}
      </text>
    </svg>
  );
}

/** Stripe wordmark — brand indigo, lowercase, as Stripe renders it. */
function StripeMark() {
  return (
    <svg
      role="img"
      aria-label="Stripe"
      viewBox="0 0 52 24"
      className="h-8 w-auto max-w-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <text
        x="0"
        y="18"
        fontFamily="Helvetica, Arial, sans-serif"
        fontWeight="700"
        fontSize="20"
        letterSpacing="-0.8"
        fill="#635BFF"
      >
        stripe
      </text>
    </svg>
  );
}

const MARK_RENDERERS: Record<string, () => ReactElement> = {
  trustpilot: TrustpilotMark,
  google: GoogleMark,
  stripe: StripeMark,
};

/* ── Section ─────────────────────────────────────────────────────────────── */

export interface ConversionTrustSectionProps {
  /**
   * Extra classes on the outer <section>. The ONLY configuration point — a
   * page may need a top border where the hero above it has none. Nothing here
   * may change what the section CLAIMS.
   */
  className?: string;
  /** Source-approved standards replace the default repeated credential copy. */
  children?: ReactNode;
}

export default function ConversionTrustSection({ className = "", children }: ConversionTrustSectionProps) {

  return (
    <section
      data-pt-trust-section="conversion"
      aria-label="PawTenant service standards and credentials"
      className={`w-full ${className}`.trim()}
    >
      {/* ── Strip 1 — deep PawTenant tone, four operational signals ───────── */}
      <div className="bg-[#123A33] bg-gradient-to-br from-[#164a41] via-[#123A33] to-[#0e2e29]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <ul
            className="
              grid grid-cols-2 gap-x-4 gap-y-6
              sm:grid-cols-2 sm:gap-x-8 sm:gap-y-7
              lg:grid-cols-4 lg:gap-x-6
              py-7 sm:py-8 lg:py-7
              list-none m-0
              lg:divide-x lg:divide-white/10
            "
          >
            {OPERATIONAL_ITEMS.map((item) => (
              <li
                key={item.key}
                data-pt-trust-item={item.key}
                className="flex items-start gap-3 min-w-0 lg:px-5 lg:first:pl-0 lg:last:pr-0"
              >
                <span
                  aria-hidden="true"
                  className="mt-[1px] flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/[0.07]"
                >
                  <i className={`${item.icon} text-[17px] text-[#F9A75E]`} />
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] sm:text-[14px] font-bold leading-snug text-white">
                    {item.label}
                  </span>
                  <span className="mt-1 block text-[11.5px] sm:text-[12.5px] leading-snug text-white/70">
                    {item.detail}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Strip 2 — light credential band ──────────────────────────────── */}
      <div className="bg-[#f3f7f5] border-b border-[#dce8e1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-7 sm:py-9">
          {children ?? <>
          <p className="text-center text-[10.5px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-[#54736a]">
            Trusted care. Verifiable documentation.
          </p>

          {/* Mobile keeps ONE shared left edge. Centring each item individually
              (justify-self-center) gave every row a different left margin —
              three ragged starts reading as sloppy rather than calm. */}
          <ul className="mt-5 mx-auto grid max-w-[380px] grid-cols-1 gap-x-6 gap-y-5 list-none p-0 sm:max-w-none sm:grid-cols-3">
            {CREDENTIAL_ITEMS.map((item) => (
              <li
                key={item.key}
                data-pt-trust-item={item.key}
                className="flex items-start gap-3"
              >
                <i
                  className={`${item.icon} flex h-11 w-11 items-center justify-center rounded-xl bg-white text-[24px] text-[#1A5C4F] ring-1 ring-[#dce8e1] flex-shrink-0`}
                  aria-hidden="true"
                />
                <span className="min-w-0">
                  <span className="block text-[14px] font-bold leading-snug text-slate-800">
                    {item.label}
                  </span>
                  <span className="mt-1 block text-[12px] leading-relaxed text-slate-500">
                    {item.detail}
                  </span>
                </span>
              </li>
            ))}
          </ul>

          </>}
          <ConversionTrustMarks />
        </div>
      </div>
    </section>
  );
}

// Reuse verified marks when supplied page copy already renders the standards.
export function ConversionTrustMarks() {
  const marks = [...EXTERNAL_MARKS, ...MEDIA_LOGOS];
  return <>{marks.length > 0 && (
            <div className="mt-7 border-t border-[#dce8e1] pt-6">
              {/* Equal tiles: two columns on phones, four from tablet upward. */}
              <ul className="mx-auto grid max-w-4xl grid-cols-2 items-stretch gap-3 list-none my-0 p-0 sm:grid-cols-4 sm:gap-4">
                {marks.map((mark) => {
                  const Mark = MARK_RENDERERS[mark.key];
                  const body = (
                    <>
                      <span className="flex h-16 w-full min-w-0 items-center justify-center">{Mark ? <Mark /> : null}</span>
                      <span className="text-center text-[11px] sm:text-xs font-medium leading-snug text-slate-600">
                        {mark.caption}
                      </span>
                    </>
                  );
                  return (
                    <li
                      key={mark.key}
                      data-pt-trust-mark={mark.key}
                      className="flex min-w-0 flex-col items-center justify-center gap-2 rounded-xl border border-[#dce8e1] bg-white px-3 py-4"
                    >
                      {mark.linked ? (
                        // href is ALWAYS mark.evidence — never a separate field.
                        // The mark and the proof are the same click, and there is
                        // no other destination a mark can be pointed at.
                        <a
                          href={mark.evidence}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          data-pt-trust-link={mark.key}
                          className="flex w-full flex-col items-center gap-2 rounded-md outline-none transition-opacity hover:opacity-70 focus-visible:ring-2 focus-visible:ring-[#1A5C4F]/40"
                        >
                          {body}
                        </a>
                      ) : (
                        body
                      )}
                    </li>
                  );
                })}
                <li data-pt-trust-mark="hipaa" className="flex min-w-0 flex-col items-center justify-center gap-2 rounded-xl border border-[#dce8e1] bg-white px-3 py-4">
                  <span className="relative flex h-16 w-full max-w-[176px] items-center justify-center overflow-hidden"><img src="/images/trust/owner-privacy-badge.jpg" alt="" width="612" height="396" loading="lazy" decoding="async" className="absolute w-full max-w-none h-auto" /></span>
                  <span className="text-center text-[11px] sm:text-xs font-medium leading-snug text-slate-600">HIPAA Compliant</span>
                </li>
              </ul>
            </div>
          )}</>;
}
